import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Box, Code, PenTool, MessageSquare, DollarSign, Grid, Shield, Layout, Server, Globe, Database, Cpu } from 'lucide-react';
import RAW_CATALOG from '../data/catalogSnapshot.json';

// Utility to get unique industries and their counts
const getDomains = () => {
    const counts = {};
    RAW_CATALOG.forEach(p => {
        p.industries.forEach(i => {
            counts[i] = (counts[i] || 0) + 1;
        });
    });
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1]) // Sort by count desc
        .map(([name, count]) => ({ name, count }));
};

const DomainCard = ({ name, count, icon: Icon }) => (
    <Link to={`/domain/${name.toLowerCase()}`} className="group relative overflow-hidden bg-[var(--bg-main)] border border-[var(--border-main)] hover:border-[var(--fg-secondary)] transition-all p-6 aspect-square flex flex-col justify-between">
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <Icon size={64} strokeWidth={1} />
        </div>

        <div>
            <Icon size={24} className="text-[var(--fg-secondary)] mb-4 group-hover:text-[var(--fg-primary)] transition-colors" />
            <h3 className="font-serif text-xl text-[var(--fg-primary)]">{name}</h3>
        </div>

        <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-[var(--fg-tertiary)] uppercase tracking-wider group-hover:text-[var(--fg-secondary)] transition-colors">
                {count} Objects
            </span>
            <div className="h-px flex-1 bg-[var(--border-main)] group-hover:bg-[var(--fg-tertiary)] transition-colors"></div>
        </div>
    </Link>
);

const DomainsPage = () => {
    useEffect(() => {
        document.title = "AltFinder / Domain Taxonomy";
    }, []);

    const domains = getDomains();

    // Mapping icons to common industry names
    const getIcon = (name) => {
        const n = name.toLowerCase();
        if (n.includes('dev') || n.includes('code')) return Code;
        if (n.includes('design') || n.includes('creative')) return PenTool;
        if (n.includes('comm') || n.includes('chat')) return MessageSquare;
        if (n.includes('finance')) return DollarSign;
        if (n.includes('prod') || n.includes('project')) return Grid;
        if (n.includes('priv') || n.includes('secur')) return Shield;
        if (n.includes('cloud') || n.includes('storage')) return Server;
        if (n.includes('web') || n.includes('browser')) return Globe;
        if (n.includes('data')) return Database;
        if (n.includes('tech') || n.includes('os')) return Cpu;
        return Box;
    };

    return (
        <AppLayout currentCategory="domains">
            <div className="h-full flex flex-col bg-[var(--bg-main)]">
                {/* Header */}
                <div className="h-24 min-h-[6rem] border-b border-[var(--border-main)] flex items-end pb-6 px-10">
                    <div>
                        <h1 className="font-serif text-4xl text-[var(--fg-primary)] mb-2">Domain Index</h1>
                        <p className="font-mono text-xs text-[var(--fg-tertiary)] uppercase tracking-widest">
                            Categorical Taxonomy of {Object.values(domains).reduce((a, b) => a + b.count, 0)} Classified Systems
                        </p>
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto p-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {domains.map(d => (
                            <DomainCard key={d.name} name={d.name} count={d.count} icon={getIcon(d.name)} />
                        ))}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default DomainsPage;
